import { LaskunListaustietorivi, LaskunListaustyyppi, LaskunTila, LaskutHakuPyynto } from '@lemon/core-firebase/model/lasku'
import { NumberDate } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

export interface LaskuhaunEhdot {
  hakuteksti: string
  viimeiset12kk: boolean
  alkuPvm: NumberDate | null
  loppuPvm: NumberDate | null
  tilat: LaskunTila[]
  asiakasAvaimet: string[]
  projektiAvaimet: string[]
  sivu: number
  sivunKoko: number
}

export interface AikavalinSanat {
  alkaen: string
  paattyen: string
}

export function onkoLaskuhaullaEhtoja(ehdot: LaskuhaunEhdot): boolean {
  return (
    !!ehdot.hakuteksti.trim() ||
    ehdot.viimeiset12kk ||
    ehdot.alkuPvm !== null ||
    ehdot.loppuPvm !== null ||
    ehdot.tilat.length > 0 ||
    ehdot.asiakasAvaimet.length > 0 ||
    ehdot.projektiAvaimet.length > 0
  )
}

export function rakennaLaskuhakupyynto(ehdot: LaskuhaunEhdot, tanaan: NumberDate, dateService: DateService): LaskutHakuPyynto | null {
  if (!onkoLaskuhaullaEhtoja(ehdot)) {
    return null
  }
  const pyynto: LaskutHakuPyynto = { ylaraja: ehdot.sivunKoko, sivu: ehdot.sivu }

  const teksti = ehdot.hakuteksti.trim()
  if (teksti) {
    pyynto.vapaahaku = teksti
  }

  if (ehdot.viimeiset12kk) {
    pyynto.alkuPvm = dateService.lisaaKuukausiaNumero(tanaan, -12)
    pyynto.loppuPvm = tanaan
  } else {
    if (ehdot.alkuPvm) {
      pyynto.alkuPvm = ehdot.alkuPvm
    }
    if (ehdot.loppuPvm) {
      pyynto.loppuPvm = ehdot.loppuPvm
    }
  }

  if (ehdot.tilat.length > 0) {
    pyynto.tilat = [...ehdot.tilat]
  }
  if (ehdot.asiakasAvaimet.length > 0) {
    pyynto.asiakasAvaimet = [...ehdot.asiakasAvaimet]
  }
  if (ehdot.projektiAvaimet.length > 0) {
    pyynto.projektiAvaimet = [...ehdot.projektiAvaimet]
  }
  return pyynto
}

/**
 * Hakutulosten yhteissumma euroina. Summa näytetään vain, kun kaikki osumat ovat samalla sivulla, koska muuten se ei
 * vastaisi hakua. Muun valuutan laskulla käytetään laskulle tallennetulla kurssilla laskettua euromäärää; jos se
 * puuttuu yhdeltäkin laskulta, yhteissummaa ei voi laskea.
 */
export function laskeHakutulostenYhteissumma(rivit: LaskunListaustietorivi[], kokonaismaara: number, verottomana: boolean, currencyService: CurrencyService): number | null {
  const laskurivit = rivit.filter(rivi => rivi.tyyppi === LaskunListaustyyppi.TAVALLINEN || rivi.tyyppi === LaskunListaustyyppi.VANHEMPI)
  const kaikkiOsumatNakyvissa = kokonaismaara > 0 && kokonaismaara === laskurivit.length
  if (!kaikkiOsumatNakyvissa) {
    return null
  }
  let yhteissumma = 0
  for (const rivi of laskurivit) {
    const rivinSumma = annaRivinSummaEuroina(rivi, verottomana)
    if (rivinSumma === null) {
      return null
    }
    yhteissumma += rivinSumma
  }
  return currencyService.roundHalfUp(yhteissumma, 2)
}

function annaRivinSummaEuroina(rivi: LaskunListaustietorivi, verottomana: boolean): number | null {
  if (rivi.valuutta === 'EUR') {
    return (verottomana ? rivi.summaAlv0 : rivi.summa) ?? 0
  }
  return (verottomana ? rivi.summaAlv0Euroina : rivi.summaEuroina) ?? null
}

export function annaAikavalinTeksti(alku: string | null, loppu: string | null, sanat: AikavalinSanat): string {
  if (alku && loppu) {
    return `${alku} - ${loppu}`
  }
  if (alku) {
    return `${sanat.alkaen} ${alku}`
  }
  if (loppu) {
    return `${sanat.paattyen} ${loppu}`
  }
  return ''
}

/** Hakusuodattimen valintaruutu: lisää arvon valittuihin tai poistaa sen, valintajärjestys säilyy. */
export function vaihdaValinta<T>(valitut: T[], arvo: T): T[] {
  return valitut.includes(arvo) ? valitut.filter(valittu => valittu !== arvo) : [...valitut, arvo]
}

/**
 * Kertoo, osuiko klikkaus johonkin annetuista alueista. Material poistaa valikon tai kalenterin DOMista jo ennen kuin
 * klikkaus kuplii dokumenttiin, kun animaatiot ovat pois päältä, joten kohde tarkistetaan tapahtuman polusta eikä
 * kohteen nykyisistä vanhemmista.
 */
export function osuikoKlikkausAlueelle(event: Event, luokat: string[]): boolean {
  return event.composedPath().some(kohde => kohde instanceof Element && luokat.some(luokka => kohde.classList.contains(luokka)))
}
